import { useEffect, useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useAuthStore } from "@safers/auth";
import { createQueryClient } from "./client";

interface QueryProviderProps {
  children: ReactNode;
  /** Devtools 를 **허용**할지. 각 앱이 import.meta.env.DEV 를 전달한다(실제 노출은 아래 옵트인). */
  devtools?: boolean;
}

const DEVTOOLS_KEY = "safers:rq-devtools";

/**
 * 개발 중이라도 기본은 꺼 둔다 — 플로팅 버튼이 화면 우하단에 고정이라 모바일 하단
 * 내비게이션 위에 겹쳐 실기기 화면 확인을 가린다.
 *
 * 켤 때는 주소에 `?devtools=1`, 끌 때는 `?devtools=0`. 선택은 그 브라우저에 남으므로
 * 폰에서도 주소를 한 번만 고치면 되고 dev 서버를 다시 띄울 필요가 없다.
 */
function devtoolsOptIn(allowed: boolean): boolean {
  if (!allowed) return false;
  try {
    const q = new URLSearchParams(window.location.search).get("devtools");
    if (q != null) localStorage.setItem(DEVTOOLS_KEY, q === "1" ? "1" : "0");
    return localStorage.getItem(DEVTOOLS_KEY) === "1";
  } catch {
    // 시크릿 모드 등에서 localStorage 접근이 막히면 그냥 안 띄운다.
    return false;
  }
}

/**
 * 공통 서버 상태 프로바이더. main.tsx 에서 AuthProvider 안쪽에 배치한다.
 * QueryClient 는 마운트당 1회만 생성(useState 초기화 함수).
 */
export function QueryProvider({ children, devtools = false }: QueryProviderProps) {
  const [client] = useState(createQueryClient);
  const [showDevtools] = useState(() => devtoolsOptIn(devtools));

  // 로그아웃/401 리셋으로 user가 null이 되면 이전 계정의 캐시를 제거한다.
  // 같은 탭에서 계정 전환 시 이전 계정 서버 데이터가 노출되는 누출을 막는다(401 자동 로그아웃 포함).
  useEffect(() => {
    let prevUser = useAuthStore.getState().user;
    return useAuthStore.subscribe((state) => {
      if (prevUser && !state.user) client.clear();
      prevUser = state.user;
    });
  }, [client]);

  return (
    <QueryClientProvider client={client}>
      {children}
      {showDevtools ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  );
}
